/**
 * RFIThread — the conversation view of an RFI (Feb 2026 — Thread redesign).
 *
 * An RFI is a project-siloed thread: original question is message #0,
 * replies append. Each message can carry its own attachments — pulled
 * from clips/drawings/documents/photos via the unified AttachPickerModal.
 *
 * Field-first mobile UX: vertically stacked, big attach button, reply
 * box always at the bottom. Right-pane context (linked drawing, location,
 * etc.) is rendered by the parent (Dialog), not here.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Paperclip, X, FileText, Layers, Pen, Image as ImageIcon, Send, Palette, LayoutGrid, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import AttachPickerModal from './AttachPickerModal';
import { SwatchStrip, attachmentFace, previewSrc } from './AttachmentChipParts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIND_ICON = {
  clip: Layers, drawing: Pen, document: FileText, image: ImageIcon, file: FileText,
  palette: Palette, board: LayoutGrid,
};

const KIND_COLOR = {
  clip: '#70170F', drawing: '#1E40AF', document: '#475569', image: '#047857', file: '#475569',
};

// RS-3a — the two new kinds borrow colours this file already carries.
KIND_COLOR.palette = KIND_COLOR.document;
KIND_COLOR.board = KIND_COLOR.drawing;

export default function RFIThread({ rfi, projectId, currentUserName = 'You', onUpdated }) {
  const [picker, setPicker] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  // Derive thread from rfi.thread, fall back to building from responses[]
  // for legacy RFIs that don't have thread[] populated yet.
  // RS-8 — a vendor's answer arrives on responses[], not on the thread: it is
  // written at their own address with no login. It reads in the thread all the
  // same, labelled for what it is.
  // RS-9 — and a GC's reply to his own sub arrives the same way, from his
  // Site Office rather than from the desk.
  const vendorAnswers = (rfi.responses || [])
    .filter(r => r.origin === 'vendor' || r.responder_role === 'vendor'
      || r.origin === 'party' || r.responder_role === 'gc')
    .map(r => ({
      id: r.id,
      author_name: r.responder_name,
      author_type: (r.origin === 'party' || r.responder_role === 'gc') ? 'gc' : 'vendor',
      choice: r.choice || '',
      body: r.response_text || '',
      attachments: r.attachments || [],
      created_at: r.created_at,
    }));

  const messages = (() => {
    if (Array.isArray(rfi.thread) && rfi.thread.length > 0) {
      return [...rfi.thread, ...vendorAnswers].sort(
        (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
    }
    const fromResponses = (rfi.responses || []).filter(
      r => r.origin !== 'vendor' && r.responder_role !== 'vendor'
        && r.origin !== 'party' && r.responder_role !== 'gc').map(r => ({
      id: r.id,
      author_name: r.responder_name,
      author_type: r.responder_role === 'designer' ? 'internal' : 'external',
      body: r.response_text,
      attachments: r.attachments || [],
      created_at: r.created_at,
    }));
    // Synth a message #0 for the original question.
    const origin = {
      id: `${rfi.id}-origin`,
      author_name: rfi.raised_by_name || rfi.submitted_by || 'Unknown',
      author_type: rfi.raised_by_type || 'internal',
      body: rfi.question || '',
      attachments: rfi.attachments || [],
      created_at: rfi.submitted_at || rfi.created_at,
    };
    return [origin, ...fromResponses, ...vendorAnswers].sort(
      (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
  })();

  const handleSendReply = async () => {
    if (!replyBody.trim() && replyAttachments.length === 0) {
      toast.error('Type a reply or attach something first');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/rfis/${rfi.id}/messages`, {
        body: replyBody.trim(),
        author_type: 'internal',
        author_name: currentUserName,
        attachments: replyAttachments,
      });
      toast.success('Reply added to thread');
      setReplyBody('');
      setReplyAttachments([]);
      onUpdated?.();
    } catch (e) {
      console.error('[RFIThread] reply failed:', e);
      toast.error('Failed to add reply');
    } finally {
      setSending(false);
    }
  };

  const removeReplyAttachment = (refId) => {
    setReplyAttachments(arr => arr.filter(a => a.ref_id !== refId));
  };

  return (
    <div className="space-y-3" data-testid="rfi-thread">
      {/* Thread messages */}
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {messages.map((m, i) => (
          <ThreadMessage key={m.id || i} message={m} isOrigin={i === 0} />
        ))}
      </div>

      {/* Reply box */}
      <div className="border-t border-[#2A2A2A] pt-3 space-y-2" data-testid="rfi-thread-reply-box">
        <Textarea
          data-testid="rfi-thread-reply-input"
          placeholder="Type a reply…"
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          className="bg-[#1A1A1A] border-[#2A2A2A] text-white min-h-[80px]"
        />

        {replyAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="rfi-thread-reply-attachments">
            {replyAttachments.map(a => (
              <AttachmentChip key={a.ref_id} attachment={a} onRemove={() => removeReplyAttachment(a.ref_id)} />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            data-testid="rfi-thread-attach-btn"
            variant="outline"
            size="sm"
            onClick={() => setPicker(true)}
            className="border-[#2A2A2A]"
          >
            <Paperclip className="w-4 h-4 mr-1" />
            Attach
          </Button>
          <Button
            type="button"
            data-testid="rfi-thread-send-reply-btn"
            size="sm"
            disabled={sending}
            onClick={handleSendReply}
            className="bg-[#70170F] hover:bg-[#70170F]/90 ml-auto"
          >
            <Send className="w-4 h-4 mr-1" />
            {sending ? 'Sending…' : 'Post reply'}
          </Button>
        </div>
      </div>

      <AttachPickerModal
        open={picker}
        onOpenChange={setPicker}
        projectId={projectId}
        alreadyAttachedIds={replyAttachments.map(a => a.ref_id)}
        onAttach={(att) => setReplyAttachments(arr => [...arr, att])}
      />
    </div>
  );
}

function ThreadMessage({ message, isOrigin }) {
  const isVendor = message.author_type === 'vendor';
  const isGc = message.author_type === 'gc';
  const isInternal = message.author_type === 'internal';
  const when = message.created_at ? new Date(message.created_at).toLocaleString() : '';
  return (
    <div
      data-testid={`rfi-thread-msg-${message.id}`}
      className={`rounded border ${isOrigin ? 'border-[#70170F]/40 bg-[#70170F]/10' : 'border-[#2A2A2A] bg-[#1A1A1A]'} p-3`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-xs text-gray-400">
          <span className="font-medium text-white">{message.author_name || 'Unknown'}</span>
          <span className="ml-2 px-1.5 py-0.5 rounded bg-[#0F0F0F] text-[10px] uppercase tracking-wider">
            {isGc ? 'GC' : isVendor ? 'Vendor' : isInternal ? 'internal' : 'external'}
          </span>
          {isOrigin && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-[#70170F]">Original question</span>
          )}
        </div>
        <div className="text-[11px] text-gray-500 flex-shrink-0">{when}</div>
      </div>
      {message.choice ? (
        <div className="text-sm text-white mb-1" data-testid={`rfi-thread-choice-${message.id}`}>
          <span className="text-gray-400">Chose: </span>
          <b>{message.choice}</b>
        </div>
      ) : null}
      <div className="text-sm text-white whitespace-pre-wrap">
        {message.body || (message.choice
          ? <span className="text-gray-500 italic">(the choice above)</span>
          : <span className="text-gray-500 italic">(no body)</span>)}
      </div>
      {(message.attachments || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {message.attachments.map(a => (
            <AttachmentChip key={a.ref_id} attachment={a} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AttachmentChip({ attachment, onRemove }) {
  // RS-3a — a palette wears its own five swatches and a board its thumbnail;
  // the picture opens in the viewer and "Open" goes to the record itself.
  const [viewing, setViewing] = useState(false);
  const Icon = KIND_ICON[attachment.kind] || FileText;
  const color = KIND_COLOR[attachment.kind] || '#475569';
  const face = attachmentFace(attachment);
  const picture = previewSrc(attachment);
  const isKind = attachment.kind === 'palette' || attachment.kind === 'board';
  return (
    <div
      data-testid={`rfi-attachment-chip-${attachment.ref_id}`}
      className="flex items-center gap-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1 text-xs"
    >
      <button
        type="button"
        data-testid={`rfi-attachment-open-preview-${attachment.ref_id}`}
        onClick={() => picture && setViewing(true)}
        className="flex items-center gap-2 min-w-0 text-left"
      >
        {attachment.kind === 'palette' ? (
          <SwatchStrip swatches={attachment.swatches} testId={`rfi-attachment-swatches-${attachment.ref_id}`} />
        ) : (attachment.thumbnail_url || (attachment.kind === 'board' && picture)) ? (
          <img
            src={attachment.thumbnail_url || picture}
            alt={face.title}
            className="w-8 h-8 object-cover rounded"
          />
        ) : (
          <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
        )}
        <span className="min-w-0 max-w-[160px] block">
          <span className="block font-medium text-white truncate">{face.title}</span>
          <span className="block text-xs uppercase tracking-wider" style={{ color }}>{face.micro}</span>
        </span>
      </button>
      {isKind && attachment.url && (
        <a
          href={attachment.url}
          data-testid={`rfi-attachment-goto-${attachment.ref_id}`}
          className="text-gray-500 hover:text-white"
          title="Open"
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
      {onRemove && (
        <button type="button" onClick={onRemove} className="ml-1 text-gray-500 hover:text-white" aria-label="Remove">
          <X className="w-3 h-3" />
        </button>
      )}
      <Dialog open={viewing} onOpenChange={setViewing}>
        <DialogContent
          className="max-w-4xl"
          data-testid={`rfi-attachment-viewer-${attachment.ref_id}`}
        >
          <img src={picture} alt={face.title} className="w-full h-auto rounded" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
